/**
 * useAgGridColumns - Hook para definir las columnas de la tabla AG Grid de Facturas (shipments)
 *
 * Alineado con InvoicesOrders y PreInvoices View: mismas columnas con valueGetters sobre datos raw (shipments).
 */

import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@shared/stores/authStore';
import { TENANT_EXTERNAL_SHIPPING_TABLE_FIELDS } from '../config/externalShippingColumns';
import type { InvoiceShipment } from '../models/InvoicesView';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<any>[] => {
  return agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
    }));
};

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  isStorageType?: boolean;
}

export const useAgGridColumns = ({ selectedColumns = [], isStorageType = false }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('invoicesViewModule');
  const currency = useCurrency();
  const teamType = useAuthStore((state) => state.team?.type);
  const isExternalShipping = teamType === 'tenant_external' && !isStorageType;

  const allColumnDefs: ColDef[] = useMemo(() => {
    // 🎯 Columnas para storages (sin cambios)
    if (isStorageType) {
      const storageColumns: CustomColDef[] = [
        {
          field: 'dateRange',
          headerName: t('table.date', 'Fecha'),
          sortable: false,
          minWidth: 200,
        },
        {
          field: 'product',
          headerName: t('table.product', 'Producto'),
          sortable: false,
          minWidth: 200,
        },
        {
          field: 'productCode',
          headerName: t('table.product_code', 'Código'),
          sortable: false,
          minWidth: 150,
        },
        {
          field: 'quantity',
          headerName: t('table.quantity', 'Cantidad'),
          sortable: false,
          minWidth: 120,
        },
        {
          field: 'unitVolume',
          headerName: t('table.unit_volume', 'Volumen unitario'),
          sortable: false,
          minWidth: 150,
        },
        {
          field: 'totalVolume',
          headerName: t('table.total_volume', 'Volumen total'),
          sortable: false,
          minWidth: 150,
        },
        {
          field: 'dimensions',
          headerName: t('table.dimensions', 'Dimensiones'),
          sortable: false,
          minWidth: 180,
        },
        {
          field: 'linesCount',
          headerName: t('table.lines_count', 'Nº líneas'),
          sortable: false,
          minWidth: 120,
        },
        {
          field: 'baseAmount',
          headerName: t('table.base_amount', 'Importe base'),
          sortable: false,
          minWidth: 150,
        },
        {
          field: 'totalAmount',
          headerName: t('table.total_amount', 'Importe total'),
          sortable: false,
          minWidth: 150,
        },
      ];
      return storageColumns;
    }

    // 🎯 Columnas para shipments (alineadas con InvoicesOrders / PreInvoices View - valueGetters sobre datos raw)
    const baseColumns: CustomColDef[] = [
      {
        field: 'ref',
        headerName: t('table.order', 'Pedido'),
        sortable: true,
        minWidth: 120,
        valueGetter: (params) => params.data?.order?.ref ?? '',
      },
      {
        field: 'order_id',
        headerName: t('table.order_id', 'ID Pedido'),
        sortable: true,
        minWidth: 100,
        valueGetter: (params) => params.data?.order_id ?? params.data?.order?.id ?? '',
      },
      {
        field: 'shop',
        headerName: t('table.shop', 'Tienda'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.order?.shop?.name ?? '',
      },
      {
        field: 'client',
        headerName: t('table.client', 'Cliente'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.customer?.name ?? params.data?.order?.team?.name ?? '',
      },
      {
        field: 'payment_method',
        headerName: t('table.payment_method', 'Método de pago'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => (params.data?.order as any)?.payment_method_method ?? '',
      },
      {
        field: 'order_total',
        headerName: t('table.order_total', 'Total pedido'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => (params.data?.order as any)?.total ?? 0,
        cellRenderer: (params: any) => {
          const total = (params.data?.order as any)?.total ?? 0;
          return <span className="text-sm font-medium">{formatCurrencyAmount(total, currency)}</span>;
        },
      },
      {
        field: 'processed_at',
        headerName: t('table.shipment_date', 'Fecha de envío'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => {
          const shippingDate = params.data?.shipping_date;
          if (shippingDate && typeof shippingDate === 'object') {
            return shippingDate.date ?? '';
          }
          return shippingDate ?? '';
        },
        cellRenderer: (params: any) => {
          const shippingDate = params.data?.shipping_date;
          const dateStr = shippingDate?.date || shippingDate || null;
          return <span className="text-sm">{formatDate(dateStr)}</span>;
        },
      },
      {
        field: 'type',
        headerName: t('table.type', 'Tipo'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => params.data?.type ?? '',
        cellRenderer: (params: any) => {
          const type = params.data?.type;
          const typeTranslations: Record<string, string> = {
            SHIPPING: t('table.types.shipping', 'Envío'),
            RETURN: t('table.types.return', 'Devolución'),
            RESHIPMENT: t('table.types.reshipment', 'Reenvío'),
          };
          return <span className="text-sm">{typeTranslations[type] || type || '-'}</span>;
        },
      },
      {
        field: 'internal_status',
        headerName: t('table.status', 'Estado interno'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.order?.status?.description_code ?? '',
      },
      {
        field: 'logistic_status',
        headerName: t('table.logistic_status', 'Estado logístico'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) =>
          params.data?.order?.shipment_status?.name ?? t('table.no_logistic_status', 'Sin estado logístico'),
      },
      {
        field: 'courier',
        headerName: t('table.courier', 'Courier'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.courier?.name ?? params.data?.order?.shipment?.courier?.name ?? '',
      },
      {
        field: 'tracking_number',
        headerName: t('table.tracking_number', 'Tracking'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => {
          const row = params.data as InvoiceShipment | undefined;
          if (!row) return '';
          return row.tracking_number ?? row.order?.tracking_number ?? row.order?.shipment?.tracking_number ?? '';
        },
      },
      {
        field: 'country',
        headerName: t('table.country', 'País'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.country ?? '',
      },
      {
        field: 'city',
        headerName: t('table.city', 'Ciudad'),
        sortable: false,
        minWidth: 140,
        valueGetter: (params) => params.data?.city ?? '',
      },
      {
        field: 'zip',
        headerName: t('table.zip', 'CP'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.zip ?? '',
      },
      {
        field: 'num_items',
        headerName: t('table.num_items', 'N° Items'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.num_items ?? 0,
      },
      {
        field: 'weight_internal_kg',
        headerName: t('table.weight_internal_kg', 'Peso (kg)'),
        sortable: false,
        minWidth: 100,
        valueGetter: (params) => params.data?.weight_internal_kg ?? params.data?.weight_courier_kg ?? 0,
        cellRenderer: (params: any) => {
          const weight = params.data?.weight_internal_kg ?? params.data?.weight_courier_kg ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(weight)}{' '}
              kg
            </span>
          );
        },
      },
      {
        field: 'period',
        headerName: t('table.period', 'Período'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.period ?? '',
      },
      {
        field: 'shipping_base',
        headerName: t('table.shipping', 'Envío'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const shippingLine = params.data?.lines?.find((l: any) => l.concept === 'SHIPPING');
          return shippingLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const shippingLine = params.data?.lines?.find((l: any) => l.concept === 'SHIPPING');
          const amount = shippingLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'preparation_base',
        headerName: t('table.preparation', 'Preparación'),
        sortable: false,
        minWidth: 130,
        valueGetter: (params) => {
          const preparationLine = params.data?.lines?.find((l: any) => l.concept === 'PREPARATION');
          return preparationLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const preparationLine = params.data?.lines?.find((l: any) => l.concept === 'PREPARATION');
          const amount = preparationLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'cod_base',
        headerName: t('table.cod', 'COD'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const codLine = params.data?.lines?.find((l: any) => l.concept === 'COD');
          return codLine?.base_amount ?? 0;
        },
        cellRenderer: (params: any) => {
          const codLine = params.data?.lines?.find((l: any) => l.concept === 'COD');
          const amount = codLine?.base_amount ?? 0;
          return (
            <span className="text-sm">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(amount)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'total',
        headerName: t('table.total', 'Total'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const lines = params.data?.lines || [];
          return lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);
        },
        cellRenderer: (params: any) => {
          const lines = params.data?.lines || [];
          const total = lines.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0);
          return (
            <span className="text-sm font-medium">
              {new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(total)}{' '}
              {currency}
            </span>
          );
        },
      },
      {
        field: 'financial_status',
        headerName: t('table.financial_status', 'Estado Financiero'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data?.financial_status ?? '',
        cellRenderer: (params: any) => {
          const status = params.data?.financial_status;
          const statusTranslations: Record<string, string> = {
            NOT_INVOICED: t('filters.financialStatusOptions.notInvoiced', 'No facturado'),
            PREINVOICED: t('filters.financialStatusOptions.preinvoiced', 'Prefacturado'),
            INVOICED: t('filters.financialStatusOptions.invoiced', 'Facturado'),
            BLOCKED: t('filters.financialStatusOptions.blocked', 'Bloqueado'),
          };
          return <span className="text-sm">{statusTranslations[status] || status || '-'}</span>;
        },
      },
      {
        field: 'pre_invoice_id',
        headerName: t('table.pre_invoice_no', 'N° Pre-Factura'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) => params.data?.pre_invoice?.code ?? params.data?.pre_invoice_id ?? '',
      },
      {
        field: 'invoice_id',
        headerName: t('table.invoice_no', 'N° Factura'),
        sortable: false,
        minWidth: 180,
        valueGetter: (params) =>
          params.data?.invoice?.code ?? (params.data?.invoice as any)?.invoice_number ?? params.data?.invoice_id ?? '',
      },
    ];

    if (!isExternalShipping) {
      return baseColumns;
    }

    const byField = new Map(baseColumns.map((col) => [col.field as string, col]));
    return TENANT_EXTERNAL_SHIPPING_TABLE_FIELDS.map((field) => byField.get(field)).filter((col): col is CustomColDef =>
      Boolean(col),
    );
  }, [t, currency, isStorageType, isExternalShipping]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  const filteredColumnDefs = useMemo(() => {
    if (isExternalShipping) {
      return allColumnDefs;
    }

    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    const filtered = allColumnDefs.filter((col) => {
      const field = col.field || '';
      return selectedColumns.includes(field);
    });
    return filtered.length > 0 ? filtered : allColumnDefs;
  }, [allColumnDefs, selectedColumns, isExternalShipping]);

  return {
    allColumnDefs: filteredColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
